import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, 
  Building2, 
  Shield, 
  Upload, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  ArrowLeftRight,
  DollarSign,
  FileText,
  CreditCard,
  Loader2,
  Info,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const KYC_DOCUMENT_TYPES = [
  { id: 'passport', label: 'Passport', description: 'International passport (single document)' },
  { id: 'drivers_license', label: "Driver's License", description: 'Front and back required' },
  { id: 'national_id', label: 'National ID Card', description: 'Front and back required' }
];

export default function MelaninAccount({ onConversionComplete }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  
  // Create account state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // KYC state
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [kycStep, setKycStep] = useState(1); // 1: select doc type, 2: upload front, 3: upload back (if needed), 4: confirm
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [kycSession, setKycSession] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Conversion state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertDirection, setConvertDirection] = useState('to_mel'); // 'to_mel' or 'to_usd'
  const [convertAmount, setConvertAmount] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/melanin-account/status`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        if (data.account) {
          setAccount(data.account);
          fetchTransactions();
        }
      }
    } catch (error) {
      console.error('Error fetching Melanin Account status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await fetch(`${API}/melanin-account/transactions`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleCreateAccount = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/melanin-account/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ account_name: accountName || undefined })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Melanin Account created successfully!');
        setAccount(data.account);
        setCreateDialogOpen(false);
        fetchStatus();
      } else {
        toast.error(data.detail || 'Failed to create account');
      }
    } catch (error) {
      toast.error('Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const handleStartKYC = async () => {
    if (!selectedDocType) {
      toast.error('Please select a document type');
      return;
    }
    
    try {
      const res = await fetch(`${API}/melanin-account/kyc/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ document_type: selectedDocType })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setKycSession(data);
        setKycStep(2);
      } else {
        toast.error(data.detail || 'Failed to start verification');
      }
    } catch (error) {
      toast.error('Failed to start verification');
    }
  };

  const handleUploadDocument = async (e, side = 'front') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingDoc(true);
    try {
      // First upload the file
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await fetch(`${API}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }
      
      const uploadData = await uploadRes.json();
      
      // Then submit to KYC
      const kycRes = await fetch(`${API}/melanin-account/kyc/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: kycSession.session_id,
          document_url: uploadData.url,
          document_side: side
        })
      });
      
      const kycData = await kycRes.json();
      
      if (kycRes.ok) {
        toast.success(`${side === 'front' ? 'Front' : 'Back'} of document uploaded`);
        
        if (kycData.next_step === 'upload_back') {
          setKycStep(3);
        } else {
          setKycStep(4);
        }
      } else {
        toast.error(kycData.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleSubmitKYC = async () => {
    try {
      const res = await fetch(`${API}/melanin-account/kyc/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: kycSession.session_id })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Documents submitted for review!');
        setKycDialogOpen(false);
        setKycStep(1);
        setSelectedDocType(null);
        setKycSession(null);
        fetchStatus();
      } else {
        toast.error(data.detail || 'Failed to submit documents');
      }
    } catch (error) {
      toast.error('Failed to submit documents');
    }
  };

  const handleConvert = async () => {
    const amount = parseFloat(convertAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setConverting(true);
    try {
      const endpoint = convertDirection === 'to_mel' 
        ? `${API}/melanin-account/convert-to-mel`
        : `${API}/melanin-account/convert-from-mel`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        setConvertDialogOpen(false);
        setConvertAmount('');
        fetchStatus();
        fetchTransactions();
        if (onConversionComplete) {
          onConversionComplete();
        }
      } else {
        toast.error(data.detail || 'Conversion failed');
      }
    } catch (error) {
      toast.error('Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-40 skeleton rounded-xl" />
        <div className="h-24 skeleton rounded-xl" />
      </div>
    );
  }

  // Feature not enabled
  if (!status?.feature_enabled) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-xl p-8 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-zinc-500" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Melanin Account Coming Soon</h3>
        <p className="text-zinc-400 mb-4">
          Virtual USD accounts powered by Iron are not yet available. Check back soon!
        </p>
        <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
          <Info className="w-4 h-4" />
          <span>This feature will allow you to hold USD and convert to/from MEL</span>
        </div>
      </motion.div>
    );
  }

  // No account yet
  if (!account) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="glass-card rounded-xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Melanin Account</h2>
              <p className="text-zinc-400">Virtual USD account powered by Iron</p>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Hold USD Balance</p>
                <p className="text-zinc-500 text-sm">Keep funds in a secure virtual account</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Convert to MEL</p>
                <p className="text-zinc-500 text-sm">Easily convert between USD and MEL tokens</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Bank Transfers</p>
                <p className="text-zinc-500 text-sm">Deposit and withdraw via bank transfer</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setCreateDialogOpen(true)}
            className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            data-testid="create-melanin-account-btn"
          >
            <Wallet className="w-5 h-5" />
            Create Melanin Account
          </button>
          
          {/* Powered by Iron branding */}
          <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-center gap-2 text-zinc-500 text-xs">
            <span>Powered by</span>
            <span className="font-semibold text-zinc-400">Iron</span>
            <ExternalLink className="w-3 h-3" />
          </div>
        </div>
      </motion.div>
    );
  }

  // Has account
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Account Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-900/30 via-emerald-900/20 to-zinc-900 border border-green-800/30 p-6">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-green-400 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        </div>

        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">{account.account_name}</p>
                <div className="flex items-center gap-2">
                  {account.status === 'active' ? (
                    <span className="flex items-center gap-1 text-green-400 text-xs">
                      <CheckCircle className="w-3 h-3" />
                      Active
                    </span>
                  ) : account.status === 'pending_verification' ? (
                    <span className="flex items-center gap-1 text-yellow-400 text-xs">
                      <Clock className="w-3 h-3" />
                      Pending Verification
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-400 text-xs">
                      <AlertCircle className="w-3 h-3" />
                      {account.status}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Powered by Iron */}
            <div className="flex items-center gap-1 text-zinc-500 text-xs">
              <span>Powered by</span>
              <span className="font-semibold text-zinc-400">Iron</span>
            </div>
          </div>

          {/* Balance */}
          <div className="mb-6">
            <p className="text-zinc-500 text-sm mb-1">USD Balance</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-white">
                ${account.balance?.toFixed(2) || '0.00'}
              </span>
              <span className="text-zinc-500">USD</span>
            </div>
          </div>

          {/* KYC Status */}
          {account.kyc_status !== 'approved' && (
            <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400 text-sm">
                    {account.kyc_status === 'pending' 
                      ? 'Verification in progress...'
                      : 'Identity verification required'}
                  </span>
                </div>
                {account.kyc_status === 'not_started' && (
                  <button
                    onClick={() => setKycDialogOpen(true)}
                    className="px-3 py-1 rounded-lg bg-yellow-500/20 text-yellow-400 text-sm hover:bg-yellow-500/30 transition-colors"
                    data-testid="start-kyc-btn"
                  >
                    Verify Now
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                setConvertDirection('to_mel');
                setConvertDialogOpen(true);
              }}
              disabled={account.status !== 'active' || account.balance <= 0}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-zinc-800/50 border border-zinc-700 text-white hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="convert-to-mel-btn"
            >
              <img src={MELANIN_ICON} alt="" className="w-5 h-5" />
              Convert to MEL
            </button>
            <button
              onClick={() => {
                setConvertDirection('to_usd');
                setConvertDialogOpen(true);
              }}
              disabled={account.status !== 'active'}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-zinc-800/50 border border-zinc-700 text-white hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="convert-to-usd-btn"
            >
              <DollarSign className="w-5 h-5 text-green-400" />
              Add from MEL
            </button>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
          Recent Activity
        </h3>
        
        {transactions.length > 0 ? (
          <div className="space-y-3">
            {transactions.slice(0, 5).map((txn) => (
              <div
                key={txn.transaction_id}
                className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    txn.direction === 'in' 
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {txn.type === 'conversion' ? (
                      <ArrowLeftRight className="w-5 h-5" />
                    ) : (
                      <CreditCard className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <p className="text-white text-sm">{txn.description}</p>
                    <p className="text-zinc-500 text-xs">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={`font-mono ${
                  txn.direction === 'in' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {txn.direction === 'in' ? '+' : '-'}${txn.amount?.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No transactions yet</p>
          </div>
        )}
      </div>

      {/* Create Account Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-400" />
              Create Melanin Account
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Open a virtual USD account to hold funds and convert to/from MEL tokens.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-zinc-400 text-sm mb-2 block">Account Name (Optional)</label>
              <Input
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="My Melanin Account"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            
            <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <p className="text-zinc-400 text-sm">
                <strong className="text-white">Note:</strong> You'll need to complete identity verification (KYC) to fully activate your account.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setCreateDialogOpen(false)}
              className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateAccount}
              disabled={creating}
              className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-500 disabled:opacity-50"
              data-testid="confirm-create-account-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Creating...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KYC Dialog */}
      <Dialog open={kycDialogOpen} onOpenChange={setKycDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              Identity Verification
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {kycStep === 1 && 'Select your identity document type'}
              {kycStep === 2 && 'Upload the front of your document'}
              {kycStep === 3 && 'Upload the back of your document'}
              {kycStep === 4 && 'Review and submit for verification'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Step 1: Select document type */}
            {kycStep === 1 && (
              <div className="space-y-3">
                {KYC_DOCUMENT_TYPES.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedDocType(doc.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-colors ${
                      selectedDocType === doc.id
                        ? 'bg-yellow-500/10 border-yellow-500/50'
                        : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className={`w-5 h-5 ${
                        selectedDocType === doc.id ? 'text-yellow-400' : 'text-zinc-400'
                      }`} />
                      <div>
                        <p className="text-white font-medium">{doc.label}</p>
                        <p className="text-zinc-500 text-sm">{doc.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Upload front */}
            {kycStep === 2 && (
              <div className="space-y-4">
                <div className="p-6 rounded-xl border-2 border-dashed border-zinc-700 text-center">
                  <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
                  <p className="text-white mb-2">Upload front of your {selectedDocType?.replace('_', ' ')}</p>
                  <p className="text-zinc-500 text-sm mb-4">PNG, JPG up to 10MB</p>
                  <label className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 cursor-pointer">
                    {uploadingDoc ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                        Uploading...
                      </>
                    ) : (
                      'Choose File'
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUploadDocument(e, 'front')}
                      disabled={uploadingDoc}
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Step 3: Upload back */}
            {kycStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Front uploaded successfully</span>
                </div>
                <div className="p-6 rounded-xl border-2 border-dashed border-zinc-700 text-center">
                  <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
                  <p className="text-white mb-2">Upload back of your {selectedDocType?.replace('_', ' ')}</p>
                  <p className="text-zinc-500 text-sm mb-4">PNG, JPG up to 10MB</p>
                  <label className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 cursor-pointer">
                    {uploadingDoc ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                        Uploading...
                      </>
                    ) : (
                      'Choose File'
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUploadDocument(e, 'back')}
                      disabled={uploadingDoc}
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {kycStep === 4 && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Documents uploaded successfully</span>
                  </div>
                  <p className="text-zinc-400 text-sm">
                    Your documents are ready to be submitted for review. Verification typically takes 1-3 business days.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <button
              onClick={() => {
                setKycDialogOpen(false);
                setKycStep(1);
                setSelectedDocType(null);
              }}
              className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700"
            >
              Cancel
            </button>
            {kycStep === 1 && (
              <button
                onClick={handleStartKYC}
                disabled={!selectedDocType}
                className="px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-500 disabled:opacity-50"
              >
                Continue
                <ArrowRight className="w-4 h-4 inline ml-2" />
              </button>
            )}
            {kycStep === 4 && (
              <button
                onClick={handleSubmitKYC}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-500"
                data-testid="submit-kyc-btn"
              >
                Submit for Review
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-purple-400" />
              {convertDirection === 'to_mel' ? 'Convert USD to MEL' : 'Convert MEL to USD'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {convertDirection === 'to_mel' 
                ? 'Convert your USD balance to MEL tokens (1:1 rate)'
                : 'Convert your MEL tokens to USD balance (1:1 rate)'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-zinc-400 text-sm mb-2 block">
                Amount ({convertDirection === 'to_mel' ? 'USD' : 'MEL'})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">
                  {convertDirection === 'to_mel' ? '$' : ''}
                </span>
                <Input
                  type="number"
                  value={convertAmount}
                  onChange={(e) => setConvertAmount(e.target.value)}
                  placeholder="0.00"
                  className={`bg-zinc-900 border-zinc-800 text-white ${
                    convertDirection === 'to_mel' ? 'pl-7' : 'pl-3'
                  }`}
                />
                {convertDirection === 'to_usd' && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">MEL</span>
                )}
              </div>
              <p className="text-zinc-500 text-xs mt-1">
                {convertDirection === 'to_mel' 
                  ? `Available: $${account?.balance?.toFixed(2) || '0.00'}`
                  : 'Available: Check your MEL balance'}
              </p>
            </div>
            
            {convertAmount && parseFloat(convertAmount) > 0 && (
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-purple-400 text-sm">
                  You will receive: <strong>
                    {convertDirection === 'to_mel' 
                      ? `${parseFloat(convertAmount).toFixed(2)} MEL`
                      : `$${parseFloat(convertAmount).toFixed(2)} USD`}
                  </strong>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setConvertDialogOpen(false)}
              className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={handleConvert}
              disabled={converting || !convertAmount || parseFloat(convertAmount) <= 0}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50"
              data-testid="confirm-convert-btn"
            >
              {converting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Converting...
                </>
              ) : (
                'Convert'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
